import type { VariantProps } from 'class-variance-authority'
import type {
  ComponentPropsWithoutRef,
  ElementRef,
  HTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react'
import { forwardRef, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { XIcon } from '@phosphor-icons/react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva } from 'class-variance-authority'

import { Button } from './button'

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = forwardRef<
  ElementRef<typeof SheetPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & {
    allowClickThrough?: boolean
  }
>(({ className, allowClickThrough, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(
      'data-[state=open]:duration-sheet data-[state=closed]:duration-dismiss fixed inset-0 z-40 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      allowClickThrough && 'pointer-events-none',
      className,
    )}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const sheetVariants = cva(
  'data-[state=open]:duration-sheet data-[state=open]:ease-ios data-[state=closed]:duration-dismiss data-[state=closed]:ease-ios-in fixed z-50 gap-4 overflow-hidden bg-background/95 p-4 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out sm:p-6',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b border-white/10 data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t border-white/10 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-full border-r border-white/10 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:w-auto',
        right:
          'inset-y-0 right-0 h-full w-full border-l border-white/10 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:w-auto',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
)

interface SheetContentProps
  extends
    ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  noBackdrop?: boolean
  actions?: ReactNode
  hideCloseButton?: boolean
  allowClickThrough?: boolean
  defaultWidth?: number
  minWidth?: number
  maxWidth?: number
}

const SheetContent = forwardRef<
  ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(
  (
    {
      side = 'right',
      className,
      children,
      noBackdrop = false,
      actions,
      hideCloseButton = false,
      allowClickThrough = false,
      defaultWidth = 600,
      minWidth = 320,
      maxWidth = 1200,
      ...props
    },
    ref,
  ) => {
    const [width, setWidth] = useState(defaultWidth)
    const [isDragging, setIsDragging] = useState(false)
    const dragStartX = useRef<number>(0)
    const dragStartWidth = useRef<number>(0)

    const handleMouseDown = (e: ReactMouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
      dragStartX.current = e.clientX
      dragStartWidth.current = width
      document.body.style.cursor = 'col-resize'
    }

    useEffect(() => {
      const handleMouseMove = (e: MouseEvent) => {
        if (!isDragging) return

        const delta = dragStartX.current - e.clientX
        const newWidth = Math.min(
          Math.max(dragStartWidth.current + delta, minWidth),
          maxWidth,
        )
        setWidth(newWidth)
      }

      const handleMouseUp = () => {
        setIsDragging(false)
        document.body.style.cursor = ''
      }

      if (isDragging) {
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
      }

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }, [isDragging, minWidth, maxWidth])

    const handleInteractOutside = (
      event: Parameters<
        Exclude<
          ComponentPropsWithoutRef<
            typeof SheetPrimitive.Content
          >['onInteractOutside'],
          undefined
        >
      >[0],
    ) => {
      if (allowClickThrough) {
        event.preventDefault()
      }
      // If props.onInteractOutside exists, call it
      props.onInteractOutside?.(event)
    }

    return (
      <SheetPortal>
        {!noBackdrop && <SheetOverlay allowClickThrough={allowClickThrough} />}
        <SheetPrimitive.Content
          ref={ref}
          className={cn(
            sheetVariants({ side }),
            'pointer-events-auto',
            className,
          )}
          style={{
            width:
              side === 'right' || side === 'left'
                ? `min(${width}px, 100vw)`
                : undefined,
            touchAction: 'none',
            zIndex: 50,
          }}
          {...props}
          onInteractOutside={handleInteractOutside}
        >
          {/* Resize handle - hide on mobile */}
          {(side === 'right' || side === 'left') && (
            <div
              className={cn(
                'absolute top-0 hidden h-full w-1 cursor-col-resize transition-colors hover:bg-border active:bg-border sm:block',
                side === 'right' ? '-left-0.5' : '-right-0.5',
                isDragging && 'bg-border',
              )}
              onMouseDown={handleMouseDown}
            />
          )}
          <div className="absolute right-12 top-2 flex items-center gap-2 sm:top-4">
            {actions}
          </div>
          {!hideCloseButton && (
            <SheetPrimitive.Close asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 top-2 z-[100] h-8 w-8 rounded-full border border-border/50 bg-background/90 shadow-lg backdrop-blur-sm transition-all duration-200 hover:border-border hover:bg-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 dark:hover:bg-background sm:right-4 sm:top-4"
              >
                <XIcon className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </SheetPrimitive.Close>
          )}
          {children}
        </SheetPrimitive.Content>
      </SheetPortal>
    )
  },
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col space-y-2 text-center sm:text-left',
      className,
    )}
    {...props}
  />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = forwardRef<
  ElementRef<typeof SheetPrimitive.Title>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = forwardRef<
  ElementRef<typeof SheetPrimitive.Description>,
  ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
